import React, { useCallback } from 'react';
import { FD161 } from '../../../services/d206Api';
import { fmt, fmt3 } from '../helpers';

interface Tab2Props {
  details: Partial<FD161>[];
  selectedRow: number;
  isEditing: boolean;
  totalItems: number;
  setSelectedRow: (idx: number) => void;
  setDetails: React.Dispatch<React.SetStateAction<Partial<FD161>[]>>;
  deleteDetailRow: () => void;
  calcDetailAmount: (detail: Partial<FD161>) => number;
}

const Tab2Detail: React.FC<Tab2Props> = React.memo(({
  details,
  selectedRow,
  isEditing,
  totalItems,
  setSelectedRow,
  setDetails,
  deleteDetailRow,
  calcDetailAmount,
}) => {
  /** Handle quantity change on a row — recalc amount immediately (BR-05) */
  const handleQtyChange = useCallback((rowIdx: number, rawValue: string) => {
    const qty = parseFloat(rawValue) || 0;
    setDetails(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      row.fd161_15 = qty;
      row.fd161_22 = Math.round(qty * (row.fd161_14 || 0));
      next[rowIdx] = row;
      return next;
    });
  }, [setDetails]);

  // Selected row side-panel data
  const sel = details[selectedRow] as Partial<FD161> | undefined;

  return (
    <div>
      {/* Header: total items */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>
          總項數: <strong>{totalItems}</strong>
        </span>
        {isEditing && (
          <button
            className="btn btn-danger btn-sm"
            onClick={deleteDetailRow}
            disabled={details.length <= 1}
          >
            明細刪除
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="table-wrapper">
        <div className="table-scroll" style={{ maxHeight: 280 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }} data-contract-field="fd161_02">項次</th>
                <th>產品代號</th>
                <th data-contract-field="fd161_12">批號</th>
                <th className="num" data-contract-field="fd161_14">進貨單價</th>
                <th className="num" style={{ width: 110 }} data-contract-field="fd161_15">退出數量</th>
                <th data-contract-field="fd161_18">退出單位</th>
                <th className="num">現有庫存量</th>
                <th className="num" data-contract-field="fd161_22">退出金額</th>
                <th className="num" data-contract-field="fd161_161">入庫單位換算率</th>
              </tr>
            </thead>
            <tbody>
              {details.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.45)', padding: 24 }}>
                    無明細資料
                  </td>
                </tr>
              ) : (
                details.map((d, i) => (
                  <tr
                    key={i}
                    className={i === selectedRow ? 'selected' : ''}
                    onClick={() => setSelectedRow(i)}
                  >
                    <td>{d.fd161_02 || i + 1}</td>
                    <td>{(d.fd161_10 || '') + (d.fd161_11 || '')}</td>
                    <td>{d.fd161_12 || ''}</td>
                    <td className="num">{fmt3(d.fd161_14)}</td>
                    <td className="num">
                      {isEditing ? (
                        <input
                          type="number"
                          value={d.fd161_15 ?? ''}
                          onChange={e => handleQtyChange(i, e.target.value)}
                          style={{ width: 90, textAlign: 'right' }}
                          step="0.01" data-contract-field="fd161_15"
                        />
                      ) : (
                        fmt(d.fd161_15)
                      )}
                    </td>
                    <td>{d.fd161_18 || ''}</td>
                    <td className="num">{fmt(d.fd161_20)}</td>
                    <td className="num">{fmt(d.fd161_22 ?? calcDetailAmount(d))}</td>
                    <td className="num">{d.fd161_161 != null ? d.fd161_161.toFixed(4) : ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Side display fields for selected row */}
      {sel && (
        <div className="detail-panels" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div className="detail-panel">
            <div className="form-row">
              <label>色號</label>
              <input type="text" value={sel.fd161_13 || ''} readOnly style={{ width: 120 }} />
            </div>
          </div>
          <div className="detail-panel">
            <div className="form-row">
              <label>印花配色</label>
              <input type="text" value={sel.fd161_21 || ''} readOnly style={{ width: 120 }} />
            </div>
          </div>
          <div className="detail-panel">
            <div className="form-row">
              <label>單位換算率</label>
              <input type="text" value={sel.fd161_16 != null ? String(sel.fd161_16) : ''} readOnly style={{ width: 120 }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Tab2Detail.displayName = 'Tab2Detail';
export default Tab2Detail;
